import { Inject, Injectable } from '@angular/core';
import { AppStore } from '@app/app.store';
import { MarketingInfo } from '@app/core/services/container/marketing-info.service';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import { CleanupRegistryService } from '@app/core/services/registry/cleanup-registry.service';
import { MenuResolverService } from '@app/core/services/resolvers/menu-resolver.service';
import { LoginState } from '@app/features/auth/model/login.interface';
import { LoginStateService } from '@app/features/auth/services/login.state.service';
import { Error } from '@app/shared/models/error.interface';
import { SessionState } from '@app/shared/models/session.interface';
import { User } from '@app/shared/models/user.interface';
import { JwtHelperService } from '@auth0/angular-jwt';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { MapStateService } from '@components/data/maps/state/map-state.service';
import { TranslateService } from '@ngx-translate/core';
import { SamlRedirectInfo } from '../login.saml.service';

@Injectable()
export class LoginStateServiceImpl implements LoginStateService {
  persistent: boolean;
  initialState = {
    language: window.navigator.language.split('-')[0],
    step: 1,
    remember: false
  };

  constructor(
    protected appStore: AppStore,
    protected translateService: TranslateService,
    protected jwtService: JwtHelperService,
    protected menuResolverService: MenuResolverService,
    protected divisionContextService: DivisionContextService,
    @Inject('StateService') private stateService: StateService,
    protected mapStateService: MapStateService,
    protected cleanupRegistryService: CleanupRegistryService
  ) {
    this.persistent = true;
    if (!this.getState()) {
      this.resetState();
    }
  }

  isPersistent(): boolean {
    return this.persistent;
  }

  getState(): LoginState {
    return this.appStore.state.login;
  }

  getError(): Error | null {
    return this.appStore.state.error;
  }

  getLoading(): boolean {
    return this.appStore.state.loading;
  }

  getUsername(): string | null {
    return this.getState().username;
  }

  getStep(): number {
    return this.getState().step || 1;
  }

  getUser(): User | null {
    return this.getState().user;
  }

  getLanguage(): string {
    return this.getState().language;
  }

  getRemember(): boolean {
    return this.getState().remember;
  }

  getMarketingInfo(): MarketingInfo {
    return this.getState().appearance;
  }

  getSession(): SessionState {
    return this.appStore.state.session;
  }

  getToken(): string | null {
    return this.getSession() ? this.getSession().token : null;
  }

  isTokenValid(): boolean {
    if (!this.getSession()) {
      return true;
    }
    const token = this.getSession().token;
    return token && !this.jwtService.isTokenExpired(token);
  }

  setPersistent(persistent: boolean): void {
    this.persistent = persistent;
  }

  setState(state: LoginState, persistent?: boolean): void {
    this.appStore.state = { ...this.appStore.state, login: state };
    if (persistent || this.persistent) {
      this.appStore.saveState();
    }
  }

  resetState(): void {
    this.setState(this.initialState);
  }

  setError(error: any): void {
    this.appStore.state = { ...this.appStore.state, error };
    if (this.persistent) {
      this.appStore.saveState();
    }
  }

  setLoading(loading: boolean): void {
    this.appStore.state = { ...this.appStore.state, loading };
    if (this.persistent) {
      this.appStore.saveState();
    }
  }

  setModulesState(modulesState: any): void {
    this.appStore.state = { ...this.appStore.state, moduleState: modulesState };
    if (this.persistent) {
      this.appStore.saveState();
    }
  }

  setUsername(username: string | null): void {
    this.setState({ ...this.appStore.state.login, username }, true);
  }

  setStep(step: number): void {
    this.setState({ ...this.appStore.state.login, step }, true);
  }

  setUser(user): void {
    this.setState({ ...this.appStore.state.login, user }, true);
  }

  setLanguage(event): void {
    this.translateService.setDefaultLang(event);
    this.translateService.use(event);
    this.setState({ ...this.appStore.state.login, language: event });
  }

  setRemember(remember: boolean): void {
    this.setState({ ...this.appStore.state.login, remember });
  }

  setMarketingInfo(appearance: MarketingInfo): void {
    this.setState({ ...this.appStore.state.login, appearance });
  }

  setSession(session: SessionState): void {
    this.appStore.state = { ...this.appStore.state, session };
    if (this.persistent) {
      this.appStore.saveState();
    }
  }

  closeSession(): void {
    this.divisionContextService.resetDivisionFilter();
    this.stateService.clear();
    this.mapStateService.clear();
    this.cleanupRegistryService.cleanup();
    this.setSession({});
    this.setError(null);
    this.setModulesState({});
    this.menuResolverService.evictCache();
    if (!this.getRemember()) {
      this.setUser(null);
      this.setUsername(null);
      this.setStep(1);
    }
  }

  setAdfsToken(token: SamlRedirectInfo): void {
    this.appStore.state.moduleState['adfs'] = token;
    this.appStore.state = { ...this.appStore.state };
    this.appStore.saveState();
  }

  getAdfsToken(): SamlRedirectInfo {
    return this.appStore.state.moduleState['adfs'];
  }
}
